import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { SimpleUser } from 'app/models';
import { CommentApi } from 'app/modules/dashboard/api/comment.api';
import { Comment } from 'app/modules/dashboard/models/comments.model';
import { ModalService } from 'app/services/modal.service';
import { LanguageService } from 'app/services/language.service';
import { BehaviorSubject, Observable, Subscription, take } from 'rxjs';
import { WatchReportModal } from '../../watchReportModal/watchReportModal.component';
import { FeedDatePipe } from 'app/modules/dashboard/pipes/feed-date.pipe';
import { InputCommentComponent } from '../../../../../../../shared/components/input-comment/input-comment.component';
import { RippleModule } from 'primeng/ripple';
import { NgClass, AsyncPipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { Settings } from 'app/modules/dashboard/models/settings.model';
import { selectSettings } from 'app/store/me/me.selectors';

@Component({
  selector: 'app-comment-element',
  templateUrl: './comment-element.component.html',
  styleUrls: ['./comment-element.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    TranslateModule,
    RippleModule,
    InputCommentComponent,
    AsyncPipe,
    FeedDatePipe,
  ],
})
export class CommentElementComponent implements OnInit, OnDestroy {
  private commentApi: CommentApi = inject(CommentApi);
  private modal: ModalService = inject(ModalService);
  private store: Store = inject(Store);
  private readonly languageService: LanguageService = inject(LanguageService);
  public readonly translate: TranslateService = inject(TranslateService);

  @Input() videoCommentId: number = 0;
  @Input() user: SimpleUser = null;
  @Input() comment!: Comment;
  @Input() subComment: boolean = false;
  @Input() isActionClick: boolean = false;
  @Input() replyCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  @Input() commentType: 'watch' | 'scenic' | 'coaching' | 'mapmode' | 'workout';
  @Input() replying: boolean = false;

  @Output() newSubComment: EventEmitter<Comment> = new EventEmitter<Comment>(
    null
  );
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onViewSubReplyClick: EventEmitter<boolean> =
    new EventEmitter<boolean>(false);
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onMoreActionClick: EventEmitter<boolean> =
    new EventEmitter<boolean>(false);
  @Output() deleteCommentElement: EventEmitter<any> = new EventEmitter<any>(
    null
  );

  subscriptions: Subscription[] = [];
  more_reply_btn: string = '';
  createdAt: Date = new Date();
  modalRenderedSubscription: Subscription;
  displaySubComment: boolean = false;
  settings$: Observable<Settings> = this.store.select(selectSettings);

  deleteComment(): void {
    this.deleteCommentElement.emit({
      commentId: this.comment.id,
      isSubComment: this.subComment,
    });
  }

  sendReply(message: string) {
    this.newSubComment.emit(this.createNewSubComment(message, null));

    switch (this.commentType) {
      case 'watch':
      case 'scenic': {
        this.commentApi
          .postCommentVideo(this.videoCommentId, message, this.comment.id)
          .pipe(take(1))
          .subscribe((res) => {
            this.replying = false;
            this.replyCount.next(this.replyCount.getValue() + 1);
          });
        break;
      }
      case 'coaching':
        this.commentApi
          .postCommentCoaching(this.videoCommentId, message, this.comment.id)
          .pipe(take(1))
          .subscribe((res) => {
            this.replying = false;
            this.replyCount.next(this.replyCount.getValue() + 1);
          });
        break;
      case 'mapmode':
        this.commentApi
          .postCommentGpsOnly(
            this.videoCommentId.toString(),
            message,
            this.comment.id.toString()
          )
          .pipe(take(1))
          .subscribe((res) => {
            this.replying = false;
            this.replyCount.next(this.replyCount.getValue() + 1);
          });
        break;
      case 'workout':
        this.commentApi
          .postCommentWorkout(this.videoCommentId, message, this.comment.id)
          .pipe(take(1))
          .subscribe((res) => {
            this.replying = false;
            this.replyCount.next(this.replyCount.getValue() + 1);
          });
        break;
    }
  }

  replyClick() {
    this.replying = !this.replying;
    this.displaySubComment = true;
    this.onViewSubReplyClick.emit(true);
  }

  createNewSubComment(message: any, id: number): Comment {
    const commentDate: any = new Date();
    commentDate.setTime(commentDate / 1000);
    return {
      id: id,
      body: message,
      created_at: commentDate,
      reply_count: 0,
      user: this.user,
      parentId: this.comment.id,
      subComments: {
        loaded: false,
        loading: false,
        comments: [],
      },
    };
  }

  getSubComment(): void {
    this.onViewSubReplyClick.emit(!this.displaySubComment);
    this.displaySubComment = !this.displaySubComment;
  }

  onActionBtnClick(): void {
    if (this.isActionClick) {
      this.isActionClick = false;
    } else {
      this.isActionClick = true;
      this.onMoreActionClick.emit(true);
    }
  }

  onReportCommentClick(): void {
    const modalClass: string = 'list-comments__modal';
    this.modal.display(modalClass);
    this.modalRenderedSubscription?.unsubscribe();

    setTimeout(() => {
      this.modal.setDialogComponent(WatchReportModal);
    });

    if (this.modal.dialogComponent instanceof WatchReportModal) {
      const component = this.modal.dialogComponent as WatchReportModal;
    } else {
      this.modalRenderedSubscription = this.modal.onModalRendered.subscribe(
        (component: WatchReportModal) => {
          component.id = this.comment.id;
          component.commentReport = true;
          component.commentBody = this.comment.body;
          component.userId = this.user.id;
          component.trackSectionId = this.videoCommentId;
          component.cancel.subscribe(() => {
            this.modal.hide();
          });
        }
      );
    }
  }

  updateReplyCount() {
    this.subscriptions = [
      ...this.subscriptions,
      this.replyCount.subscribe((count) => {
        this.more_reply_btn = this.translate
          .instant('watch.comment_modal.show_more_reply_btn')
          .replace('${reply_count}', count.toString());
      }),
    ];
  }

  ngOnInit(): void {
    this.replyCount.next(this.comment.reply_count);
    const createdDate: any = this.comment.created_at;
    this.createdAt.setTime(createdDate * 1000);
    this.updateReplyCount();

    this.subscriptions = [
      ...this.subscriptions,
      this.languageService.currentLanguage$.subscribe(() => {
        this.updateReplyCount();
      }),
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
